import {
    requestDefaultBrandPromo,
    requestPromoForCode,
    calculatePromoRewards,
    requestOrderWillExceedRedemptionLimitsV4View,
    getApplicableShippingRewards
} from 'capi';
import { Map as IMap, List as IList, fromJS } from 'immutable';
import {
    put,
    call,
    select,
    takeLatest,
    take,
    all
} from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import {
    RETRIEVED_BANNER_CONFIG,
    getPromoNudgeConfig,
    PROMO_NUDGE_SUCCESS,
    PROMO_NUDGE_FAILED
} from 'capi/redux';
import { logError, reportPageLoadError } from '../utils/errorUtils';
import { getPreferredPromoCode } from '../app/bootstrap';
import { orderSerializer } from '../payment/capiSerializer';
import { getOrderMetaData } from '../payment/paymentSelectors';
import { PROMO_ERROR_RESPONSES } from '../payment/paymentModule';
import { getCapiClient } from '../session/sessionSelectors';
import { removeQueryParams } from '../routing/routing';
import { getIntlLocale, getIntlCurrencyCode } from '../intl/intlSelectors';
import { SET_CURRENCY, SET_LOCALE, INTL_CURRENCY_UPDATED } from '../intl/intlModule';

import {
    getActivePromo,
    getActivePromoCode, getCustomRewardCardRecipients, getParentCartTokenForItem, getPromoDetailsForSegment,
    getPromoStatus as getCurrentPromoStatus
} from './promoSelectors';
import {
    GET_INITIAL_PROMO,
    GET_PROMO_FOR_CODE,
    APPLY_ACTIVE_PROMO,
    INVALID_PROMO_ERROR,
    EXPIRED_PROMO_ERROR,
    SOLD_OUT_PROMO_ERROR,
    PROMO_VALID_SUCCESS,
    SET_REJECTED_PROMO,
    RESET_PROMO,
    REWARDS_REQUEST_STATE,
    setPromo,
    setPromoError,
    setPromoRewards,
    setRejectedPromo,
    setPromoStatus,
    clearPromoStatus,
    clearRejectedPromos,
    clearActivePromo,
    setRewardsRequestActive,
    setRewardsRequestFailure,
    removePromoRewards,
    setPromoByCodeRequestState,
    removeCustomRewardCardRecipients,
    setPromoLimitExceeded,
    setShowBanner,
    setCustomRewardCardRecipientEmailForPromo,
    setCustomRewardCardRecipientNameForPromo,
    REWARD_EMAIL,
    REWARD_RECIPIENT_NAME, UPDATE_CUSTOM_REWARD_CARD_SENDER,
    getPromoNudgeConfigSuccess,
    getPromoNudgeConfigFailed,
    GET_PROMO_NUDGE_CONFIG_DATA,
    finishedGettingPromoRewards,
    setDefaultPromoCode
} from './promoModule';
import {
    getAllActiveCards,
    getPaymentEGiftCards,
    getPaymentPhysicalGiftCards
} from '../productCartHandler/productCartSelectors';
import { HANDLE_SENDER_DETAILS_UPDATE, UPDATE_ITEM } from '../productCartHandler/productCartModule';
import { runAmountCalculations } from '../productCartHandler/productCartSagas';


const isSuccessStatus = status => status === PROMO_VALID_SUCCESS;

const PAYMENT_REDIRECT_PATHS = [
    '/storefront/paymentConfirmation',
    '/storefront/paymentFailed'
];
export const isPaymentRedirectPath = () => {
    const path = window && window.location && window.location.pathname;
    if (!path) return false;
    return PAYMENT_REDIRECT_PATHS.some(
        redirectPath => path.startsWith(redirectPath)
    );
};

const getPromoStatus = (promoData, applySoldOutPromo) => {
    if (!promoData || !promoData.promoCode) {
        return INVALID_PROMO_ERROR;
    }

    if (!promoData.active) {
        return EXPIRED_PROMO_ERROR;
    }

    if (promoData.soldOut && !applySoldOutPromo) {
        return SOLD_OUT_PROMO_ERROR;
    }

    return PROMO_VALID_SUCCESS;
};

function* getPromoBy(args) {
    const capiClient = yield select(getCapiClient);
    let { locale, currency } = args;
    const {
        promoFetchFn,
        getPromoArgs,
        errorReturnVal,
        codeOrDefault
    } = args;
    if (!locale) {
        locale = yield select(getIntlLocale);
    }
    if (!currency) {
        currency = yield select(getIntlCurrencyCode);
        if (!currency) {
            
            
            yield take(INTL_CURRENCY_UPDATED);
            currency = yield select(getIntlCurrencyCode);
        }
    }
    try {
        return yield call(
            promoFetchFn,
            capiClient,
            ...getPromoArgs(locale, currency)
        );
    } catch (error) {
        yield put(setPromoError(codeOrDefault, error));
        return errorReturnVal;
    }
}

function* getDefaultPromo(locale, currency) {
    return yield call(getPromoBy, {
        codeOrDefault: 'default',
        promoFetchFn: requestDefaultBrandPromo,
        getPromoArgs: (promoLocale, promoCurrency) => [promoLocale, promoCurrency],
        errorReturnVal: null,
        locale,
        currency
    });
}

function* setDefaultPromo(defaultPromoData) {
    const statusType = yield call(getPromoStatus, defaultPromoData, true);
    if (defaultPromoData && isSuccessStatus(statusType)) {
        yield put(setDefaultPromoCode(defaultPromoData.promoCode));
        yield put(setPromo(defaultPromoData));
    }
}

export function* fetchDefaultPromo() {
    const defaultPromoData = yield call(getDefaultPromo);
    yield call(setDefaultPromo, defaultPromoData);
}

function* getPromoForCode(promoCode, locale, currency) {
    yield put(setPromoByCodeRequestState(REWARDS_REQUEST_STATE.ACTIVE));
    const promo = yield call(getPromoBy, {
        codeOrDefault: promoCode,
        promoFetchFn: requestPromoForCode,
        getPromoArgs: (promoLocale, promoCurrency) => [promoCode, promoLocale, promoCurrency],
        errorReturnVal: {},
        locale,
        currency
    });
    yield put(setPromoByCodeRequestState(REWARDS_REQUEST_STATE.SUCCESS));
    return promo;
}

export function* fetchPromoForCode(action) {
    const { promoCode, applySoldOutPromo, isManual } = action;
    const promoData = yield call(getPromoForCode, promoCode);
    const statusType = getPromoStatus(promoData, applySoldOutPromo);

    if (isSuccessStatus(statusType)) {
        yield put(setPromo(promoData));
    }
    yield put(setPromoStatus({ statusType, promoCode, isManual }));

    return statusType;
}

export function* capiV4ApplyPromoRewards(
    promoCode, orderMetaData, digitalItems, physicalItems, isApplicableShipping, isManual
) {
    
    const payment = IMap({
        senderEmail: `${uuid()}@dummy.email`,
        firstName: 'DUMMYNAME'
    });
    let promoLimitExceeded = false;
    let orderData = yield call(
        orderSerializer,
        payment,
        orderMetaData,
        digitalItems,
        physicalItems
    );

    const extractPurchaserEmail = () => {
        const eGiftCards = orderData && orderData.get('egiftCards');
        const eGiftCard = eGiftCards && eGiftCards.get(0);

        if (eGiftCard) {
            return eGiftCard.get('selfBuy')
                ? eGiftCard.get('recipientEmail')
                : eGiftCard.get('senderEmail');
        }

        const shippingGroups = orderData && orderData.get('shippingGroups');
        
        
        let shippingGroup;
        if (shippingGroups) {
            if (IList.isList(shippingGroups)) {
                shippingGroup = shippingGroups.get(0);
            } else if (Array.isArray(shippingGroups)) {
                shippingGroup = IList(shippingGroups).get(0);
            } else {
                
                
                shippingGroup = shippingGroups[0];
            }
        }
        const cert = shippingGroup && shippingGroup.certs && shippingGroup.certs[0];

        return cert && cert.recipientEmail;
    };

    const purchaserEmail = extractPurchaserEmail();
    
    
    if (purchaserEmail) {
        orderData = orderData.set('purchaserEmail', purchaserEmail);
    }

    const limitValidationDataPromo = IMap({
        promoCode,
        externalIds: purchaserEmail
            ? [{ externalId: purchaserEmail, externalIdType: 'PURCHASER_EMAIL' }]
            : []
    });

    const capiClient = yield select(getCapiClient);
    
    
    
    const currentPromoStatus = yield select(getCurrentPromoStatus);
    if (currentPromoStatus
        && currentPromoStatus.get('statusType') === PROMO_ERROR_RESPONSES.PROMO_EXHAUSTED) {
        yield put(clearPromoStatus());
    }
    try {
        yield put(setRewardsRequestActive());
        let rewards;
        if (isApplicableShipping) {
            rewards = yield call(
                getApplicableShippingRewards,
                capiClient,
                promoCode,
                orderData
            );
        } else {
            rewards = yield call(
                calculatePromoRewards,
                capiClient,
                promoCode,
                orderData
            );
        }

        let limitData;
        try {
            limitData = yield call(
                requestOrderWillExceedRedemptionLimitsV4View,
                capiClient,
                limitValidationDataPromo
            );
        } catch (error) {
            logError(error, { promoCode, additionalMessage: 'Error while checking redemption limits' });
            limitData = {};
        }

        if (rewards) {
            const segmentPromoData = yield select(getPromoDetailsForSegment); 
            yield put(setPromoRewards(fromJS(rewards), segmentPromoData));
        } else {
            yield put(setPromoRewards(IMap()));
        }

        if (limitData && limitData.willExceedRedemptionLimits) {
            yield put(setPromoRewards(IMap()));
            promoLimitExceeded = rewards && rewards.cartQualifies;
        }
        yield put(setPromoLimitExceeded(promoLimitExceeded));
    } catch (error) {
        
        
        const res = error && error.response;
        if (res && res.status === 403 && res.data && res.data.errorCode === 'PROMO_LIMITED') {
            promoLimitExceeded = true;
            
            yield put(removePromoRewards());
            yield put(setPromoLimitExceeded(promoLimitExceeded));
            yield put(setPromoStatus({
                statusType: PROMO_ERROR_RESPONSES.PROMO_EXHAUSTED,
                promoCode,
                isManual
            }));
            if (isManual) {
                
                yield put(clearActivePromo());
            }
        }
        yield put(setPromoError(promoCode, error));
        yield put(setRewardsRequestFailure());
        logError(error, { promoCode, additionalMessage: 'Error while processing promo' });
        reportPageLoadError('Error in capiV4ApplyPromoRewards', error, { promoCode, additionalMessage: 'Error while processing promo' });
    }
    yield put(finishedGettingPromoRewards(capiClient, orderData));
}

export function* applyPromoRewards(action) {
    const { isApplicableShipping = false, isManual = false } = action || {};
    const activeItems = yield select(getAllActiveCards);
    const promo = yield select(getActivePromo);

    
    if (promo && promo.size && activeItems && activeItems.size) {
        const promoCode = promo.get('promoCode');
        const orderMetaData = yield select(getOrderMetaData);
        const digitalItems = yield select(getPaymentEGiftCards);
        const physicalItems = yield select(getPaymentPhysicalGiftCards);
        yield call(capiV4ApplyPromoRewards, promoCode, orderMetaData, digitalItems, physicalItems, isApplicableShipping, isManual);
    }
}

export function* applyCustomPromoRewards(action) {
    const promoItem = action.itemUpdates;
    const { parentCardCartToken, isSender } = yield select(getParentCartTokenForItem, promoItem);
    if (parentCardCartToken) {
        yield put(setCustomRewardCardRecipientEmailForPromo(parentCardCartToken,
            isSender ? (promoItem.get('senderEmail') || promoItem.get('yourEmail')) : promoItem.get('recipientEmail')));
        
        yield put(setCustomRewardCardRecipientNameForPromo(parentCardCartToken,
            isSender ? (promoItem.get('senderName') || promoItem.get('yourName'))
                : (promoItem.get('recipientName') || promoItem.get('shippingAddress')?.get('fullName')
                )));
    }
    yield call(runAmountCalculations);
}

function* handleSenderDetailsUpdate(action) {
    const { updatedSenderName, updatedSenderEmail, shouldCallApply } = action;
    const currentMap = yield select(getCustomRewardCardRecipients);
    
    const updatedMap = currentMap.mapEntries(([token, value]) => {
        const selectedRecipient = value.get('selectedRecipient');
        
        if (selectedRecipient && selectedRecipient.includes('_sender')) {
            const updatedValue = value.merge({
                [REWARD_EMAIL]: updatedSenderEmail,
                [REWARD_RECIPIENT_NAME]: updatedSenderName
            });
            return [token, updatedValue];
        }
        return [token, value]; 
    });
        
    yield put({
        type: UPDATE_CUSTOM_REWARD_CARD_SENDER, 
        updatedMap
    });
    if (shouldCallApply) {
        yield call(applyPromoRewards);
    }
}

export function* handleNonDefaultPromo(action) {
    const { isManual } = action;
    const statusType = yield call(fetchPromoForCode, action);
    if (isSuccessStatus(statusType)) {
        yield call(applyPromoRewards, { isManual });
        
        yield call(removeQueryParams, ['promoCode']);
    }
}

function* handleDefaultPromo() {
    yield call(fetchDefaultPromo);
    yield call(applyPromoRewards);
}

function* checkStatusAndSetPromo(promoCode, promoData) {
    let success = false;
    if (promoData) {
        const statusType = yield call(getPromoStatus, promoData, true);
        success = isSuccessStatus(statusType);
    }

    if (success) {
        yield put(setPromo(promoData));
    } else {
        yield put(setRejectedPromo(promoCode));
    }

    return success;
}

function* getAllPromos(preferredPromoCode, storedPromoCode, locale, currency) {
    const fetchPromoActions = { default: call(getDefaultPromo, locale, currency) };
    if (preferredPromoCode) {
        fetchPromoActions.request = call(getPromoForCode, preferredPromoCode, locale, currency);
    }
    if (storedPromoCode) {
        fetchPromoActions.stored = call(getPromoForCode, storedPromoCode, locale, currency);
    }
    return yield all(fetchPromoActions);
}

function* removeRequestPromo() {
    yield call(removeQueryParams, ['promoCode']);
}

function* clearPromotionData() {
    yield put(clearPromoStatus());
    yield put(clearActivePromo());
    yield put(removePromoRewards());
    yield put(clearRejectedPromos());
    yield put(removeCustomRewardCardRecipients());
}

function* handlePromoReset() {
    yield call(removeRequestPromo);
    yield call(clearPromotionData);
    yield call(handleDefaultPromo);
}

function* updatePromoForLanguageAndCurrencyUpdates({ locale, currency }) {
    const storedPromoCode = yield select(getActivePromoCode);

    yield put(setRewardsRequestActive());
    yield call(clearPromotionData);

    const allPromoData = yield call(getAllPromos, null, storedPromoCode, locale, currency);

    let validStoredPromo = false;
    
    if (storedPromoCode) {
        validStoredPromo = yield call(checkStatusAndSetPromo, storedPromoCode, allPromoData.stored);
    }

    
    if (allPromoData.default && !validStoredPromo) {
        yield call(setDefaultPromo, allPromoData.default);
    }

    
    yield call(applyPromoRewards);
}

export function* getPromoNudges(action) {
    
    const { promoCode, cartAmount, cardAmount } = action;
    yield put(getPromoNudgeConfig(promoCode, cartAmount, cardAmount));

    const result = yield take([
        PROMO_NUDGE_SUCCESS, PROMO_NUDGE_FAILED
    ]);
    switch (result.type) {
        case PROMO_NUDGE_SUCCESS:
            yield put(getPromoNudgeConfigSuccess(result.data));
            break;
        case PROMO_NUDGE_FAILED:
            
            logError('Error on promo nudge config call. Capi-js returned a request failure');
            reportPageLoadError('Error on promo nudge config call.', 'Error on promo nudge config call. Capi-js returned a request failure');
            
            yield put(getPromoNudgeConfigFailed());
            break;
        default:
            break;
    }
}


function* initialGetPromo({ locale, currency }) {
    const preferredPromoCode = yield call(getPreferredPromoCode);
    const storedPromoCode = yield select(getActivePromoCode);

    const allPromoData = yield call(getAllPromos, preferredPromoCode, storedPromoCode, locale, currency);

    
    let validPreferredPromo = false;
    if (preferredPromoCode) {
        validPreferredPromo = yield call(checkStatusAndSetPromo, preferredPromoCode, allPromoData.request);
    }

    let validStoredPromo = false;
    
    if (storedPromoCode && !validPreferredPromo) {
        validStoredPromo = yield call(checkStatusAndSetPromo, storedPromoCode, allPromoData.stored);
    }

    
    if (allPromoData.default && !validPreferredPromo && !validStoredPromo) {
        yield call(setDefaultPromo, allPromoData.default);
    }

    
    
    
    
    if (!isPaymentRedirectPath()) {
        
        yield call(applyPromoRewards);
    }
}

export function* handlePromoBanner(action) {
    const bannerData = action.data;
    const promoCode = bannerData[0] && bannerData[0].promo_code;
    if (promoCode) {
        const promoData = yield call(getPromoForCode, promoCode);
        const statusType = getPromoStatus(promoData, false);
        yield put(setShowBanner(isSuccessStatus(statusType)));
    } else {
        yield put(setShowBanner(true));
    }
}

export function* promoSagasWatcher() {
    yield takeLatest(GET_INITIAL_PROMO, initialGetPromo);
    yield takeLatest(GET_PROMO_FOR_CODE, handleNonDefaultPromo);
    yield takeLatest(APPLY_ACTIVE_PROMO, applyPromoRewards);
    yield takeLatest(HANDLE_SENDER_DETAILS_UPDATE, handleSenderDetailsUpdate);
    yield takeLatest(UPDATE_ITEM, applyCustomPromoRewards);
    yield takeLatest(SET_REJECTED_PROMO, removeRequestPromo);
    yield takeLatest(RESET_PROMO, handlePromoReset);
    yield takeLatest([SET_LOCALE, SET_CURRENCY], updatePromoForLanguageAndCurrencyUpdates);
    yield takeLatest(RETRIEVED_BANNER_CONFIG, handlePromoBanner);
    yield takeLatest(GET_PROMO_NUDGE_CONFIG_DATA, getPromoNudges);
}
